import {
    deletePlantNoteMutation,
    updatePlantNoteActiveMutation,
    updatePlantNoteContentMutation,
} from '@/lib/apollo/mutations';
import { getTZDate } from '@/lib/getDates';
import { useMutation } from '@apollo/client';
import { ActionIcon, Box, Button, Card, Group, Switch, Text, Textarea, ThemeIcon, Tooltip } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy, IconPencil, IconPencilOff, IconUser } from '@tabler/icons-react';
import { useSession } from 'next-auth/react';

import { StandardLoadingOverlay } from './loaders';

const NoteCard = ({ note, canDelete }) => {
    const [overlay, { toggle: toggleOverlay }] = useDisclosure(false);
    const [edit, { toggle: toggleEdit }] = useDisclosure(false);
    const { data: session } = useSession();
    const [deletePlantNote] = useMutation(deletePlantNoteMutation, {
        refetchQueries: ['getPlantNotes', 'getPlants'],
        variables: { id: note.id },
        onCompleted: () => {
            notifications.show({
                title: 'Operazione completata',
                message: 'Nota eliminata correttamente',
                color: 'teal',
            });
            toggleOverlay();
        },
    });
    const [updatePlantNote] = useMutation(updatePlantNoteActiveMutation, {
        refetchQueries: ['getPlantNotes', 'getPlants'],
        onCompleted: () => {
            notifications.show({
                title: 'Operazione completata',
                message: 'Nota aggiornata correttamente',
                color: 'teal',
            });
            toggleOverlay();
        },
    });
    const [updatePlantNoteContent] = useMutation(updatePlantNoteContentMutation, {
        refetchQueries: ['getPlantNotes', 'getPlants'],
        onCompleted: () => {
            notifications.show({
                title: 'Operazione completata',
                message: 'Nota aggiornata correttamente',
                color: 'teal',
            });
            toggleOverlay();
            toggleEdit();
        },
    });

    const form = useForm({
        validate: { content: isNotEmpty('Inserisci una nota.') },
    });

    return (
        <Card withBorder mb="sm" shadow="sm">
            <Card.Section bg="rgba(0,0,0,0.1)">
                <Group pl="md" pr="md" pt={2} pb={2} justify="space-between">
                    <Group gap="xs">
                        {canDelete && (
                            <Tooltip withArrow label={<Text fz="sm">Elimina</Text>}>
                                <ActionIcon
                                    onClick={() => {
                                        toggleOverlay();
                                        deletePlantNote();
                                    }}
                                    size={10}
                                    color="red"
                                    radius="xl"
                                />
                            </Tooltip>
                        )}
                        <Tooltip withArrow label={<Text fz="sm">Modifica nota</Text>}>
                            <ActionIcon
                                onClick={() => {
                                    toggleEdit();
                                }}
                                variant="transparent"
                                size={15}
                            >
                                {edit ? <IconPencilOff /> : <IconPencil />}
                            </ActionIcon>
                        </Tooltip>
                        <Text fz="xs">Nota del {getTZDate(new Date(note.createdDate)).toLocaleString()}</Text>
                        <Tooltip
                            withArrow
                            label={<Text fz="sm">{note.active ? 'Attiva' : 'Non attiva'}</Text>}
                            refProp="rootRef"
                        >
                            <Switch
                                size="xs"
                                checked={note.active}
                                onChange={(e) => {
                                    toggleOverlay();
                                    updatePlantNote({ variables: { id: note.id, active: e.target.checked } });
                                }}
                            />
                        </Tooltip>
                    </Group>
                    <Group gap={2}>
                        <Tooltip withArrow label={<Text fz="sm">{note.email}</Text>}>
                            <ThemeIcon color="dark" size="xs" variant="transparent">
                                <IconUser />
                            </ThemeIcon>
                        </Tooltip>
                    </Group>
                </Group>
            </Card.Section>
            <StandardLoadingOverlay visible={overlay} />
            <Box pt="xs" pb="xs" mih={40}>
                {edit ? (
                    <Box
                        component="form"
                        onSubmit={form.onSubmit((values) => {
                            toggleOverlay();
                            updatePlantNoteContent({ variables: { id: note.id, content: values.content } });
                        })}
                    >
                        <Textarea
                            size="xs"
                            placeholder="Inserisci la nota"
                            autosize
                            minRows={2}
                            maxRows={4}
                            defaultValue={note.content}
                            {...form.getInputProps('content')}
                        />
                        <Button type="submit" size="compact-xs" leftSection={<IconDeviceFloppy size=".8rem" />}>
                            Salva
                        </Button>
                    </Box>
                ) : (
                    <Text fz="xs">{note.content}</Text>
                )}
            </Box>
        </Card>
    );
};

export default NoteCard;
